import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2, Users } from "lucide-react";
import { API, CITIES, WHATSAPP_COMMUNITY_URL } from "../data/site";
import { CONDITIONS } from "../data/conditions";

export const LeadCapture = ({ dark = false, source = "website" }) => {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", city: "delhi_ncr", topics: [] });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/waitlist/count`).then((r) => setCount(r.data.total)).catch(() => {});
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { ...form, topics: form.topics.length ? form.topics : null, source });
      setDone(true);
      toast.success("You are on the list! Welcome to Pranivaa.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = dark
    ? "w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-turmeric/70 text-sm"
    : "w-full bg-paper-card border border-[#D6CEBF] rounded-xl px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-turmeric/70 text-sm";

  if (done) {
    return (
      <div
        data-testid="waitlist-success-toast"
        className={`rounded-2xl p-8 text-center ${dark ? "bg-white/10 border border-white/15" : "bg-leaf-light border border-leaf/20"}`}
      >
        <CheckCircle2 className={`mx-auto mb-3 ${dark ? "text-turmeric" : "text-leaf"}`} size={36} />
        <h3 className={`font-serif text-2xl font-semibold mb-2 ${dark ? "text-white" : "text-ink"}`}>
          You are officially early.
        </h3>
        <p className={`text-sm mb-6 ${dark ? "text-white/60" : "text-ink-soft"}`}>
          We will write to you at <span className="font-semibold">{form.email}</span>. Meanwhile, come
          say hi in the WhatsApp community.
        </p>
        <a
          href={WHATSAPP_COMMUNITY_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="community-whatsapp-button"
          className="inline-flex items-center gap-2 rounded-full bg-leaf text-white font-semibold text-sm px-6 py-3 transition-all hover:-translate-y-0.5 hover:shadow-lg"
        >
          <MessageCircle size={16} /> Join the WhatsApp Community
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="waitlist-form" className="space-y-4">
      <div className="flex gap-2" role="group" aria-label="Choose your city">
        {CITIES.map((c) => (
          <button
            key={c.id}
            type="button"
            data-testid={c.id === "delhi_ncr" ? "waitlist-city-select-delhi" : "waitlist-city-select-bengaluru"}
            onClick={() => setForm({ ...form, city: c.id })}
            className={`flex-1 rounded-full px-4 py-2.5 text-sm font-semibold border transition-all duration-300 ${
              form.city === c.id
                ? "bg-turmeric text-ink border-turmeric"
                : dark
                  ? "border-white/25 text-white/70 hover:border-white/60"
                  : "border-[#D6CEBF] text-ink-soft hover:border-ink"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <input
        data-testid="waitlist-name-input"
        className={inputCls}
        placeholder="Your name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
      />
      <input
        data-testid="waitlist-email-input"
        className={inputCls}
        type="email"
        required
        placeholder="Email address *"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
      />
      <input
        data-testid="waitlist-whatsapp-input"
        className={inputCls}
        placeholder="WhatsApp number (optional)"
        value={form.whatsapp}
        onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
      />
      <div>
        <p className={`text-xs font-mono tracking-wide mb-2 ${dark ? "text-white/50" : "text-ink-muted"}`}>
          Which guides should we send you? Pick any:
        </p>
        <div className="flex flex-wrap gap-2" data-testid="waitlist-topics-group">
          {CONDITIONS.map((c) => {
            const active = form.topics.includes(c.slug);
            return (
              <button
                key={c.slug}
                type="button"
                data-testid={`waitlist-topic-${c.slug}`}
                aria-pressed={active}
                onClick={() =>
                  setForm({
                    ...form,
                    topics: active ? form.topics.filter((t) => t !== c.slug) : [...form.topics, c.slug],
                  })
                }
                className={`rounded-full px-3.5 py-2 text-xs font-semibold border transition-all duration-300 ${
                  active
                    ? "bg-leaf text-white border-leaf"
                    : dark
                      ? "border-white/25 text-white/70 hover:border-white/60"
                      : "border-[#D6CEBF] text-ink-soft hover:border-ink bg-paper-card"
                }`}
              >
                {c.navName}
              </button>
            );
          })}
        </div>
      </div>
      <button
        data-testid="waitlist-submit-button"
        type="submit"
        disabled={loading}
        className="w-full rounded-full bg-turmeric text-ink font-bold text-sm px-6 py-4 tracking-wide transition-all duration-300 hover:bg-turmeric-deep hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
      >
        {loading ? "Saving your spot..." : "Save My Spot"}
      </button>
      {count !== null && (
        <p
          data-testid="waitlist-counter"
          className={`flex items-center justify-center gap-2 text-xs font-mono ${dark ? "text-white/50" : "text-ink-muted"}`}
        >
          <Users size={13} /> {count} {count === 1 ? "person is" : "people are"} already waiting
        </p>
      )}
    </form>
  );
};
